'use client'

import {
  ChevronDownIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import clsx from 'clsx'
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
  type Variants,
} from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useQueryState } from 'nuqs'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'

import useApiClient from '@/lib/apis/clients/useApiClient'
import { SearchMedicine } from '@/lib/apis/medicines'
import type {
  CuratedMedicineConcise,
  MedicineCategory,
  MedicineWithKeyAcg,
} from '@/lib/apis/types'
import { PricingValue } from '@/lib/cms/pricing'
import Spinner from '@/shared/components/Spinner'
import {
  softEditorialEase,
  softEditorialEaseInOut,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

import { useAnimateMedicineQuizChrome } from './MedicineQuizMotionBoundary'

type Props = {
  categories?: MedicineCategory[]
  category?: MedicineCategory
  groups?: MedicineWithKeyAcg[]
}

export type MedicineSelection = {
  href: string
  medicine: CuratedMedicineConcise
  medicineId: string
  preferredBrand?: string
}

function medicineStrength(medicine: CuratedMedicineConcise) {
  const match = medicine.displayName.match(/(\d+(?:\.\d+)?)\s*(?:mcg|mg|g)\b/i)
  return match ? Number(match[1]) : 0
}

function orderSearchGroups(groups: MedicineWithKeyAcg[]) {
  return groups.map((group) => ({
    ...group,
    medicines: [...group.medicines].sort(
      (left, right) => medicineStrength(right) - medicineStrength(left),
    ),
  }))
}

const pageMotion: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: softMotionDuration.page,
      ease: softEditorialEase,
      staggerChildren: 0.08,
    },
  },
}

const sectionMotion: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
}

const listMotion: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.02,
      staggerChildren: 0.035,
    },
  },
}

const itemMotion: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
}

const viewportItemMotion: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.48,
      ease: softEditorialEase,
    },
  }),
}

const brandListMotion: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.045,
    },
  },
}

const brandItemMotion: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.32,
      ease: softEditorialEase,
    },
  },
}

const stateMotion: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    transition: {
      duration: 0.36,
      ease: softEditorialEaseInOut,
    },
  },
}

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      className={clsx('h-[15px] w-[15px] shrink-0', {
        'rotate-180': direction === 'right',
      })}
      viewBox="0 0 24 24"
    >
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  )
}

function RadioMark({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'ml-auto flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] sm:h-[19px] sm:w-[19px]',
        selected
          ? 'border-[#0B0B0F] bg-[#0B0B0F]'
          : 'border-[#C4C4CA] bg-white',
      )}
    >
      {selected && <span className="h-[7px] w-[7px] rounded-full bg-white" />}
    </span>
  )
}

function RestrictedMedicineNotice({
  listLinkVisibility,
}: {
  listLinkVisibility: 'always' | 'mobile' | 'never'
}) {
  return (
    <motion.div
      variants={sectionMotion}
      className="mt-[18px] flex flex-col gap-2.5 sm:mt-[22px]"
    >
      <div className="flex items-start gap-2.5 rounded-[14px] bg-[#FBF3E3] px-3.5 py-3 text-[#8A5A1F] sm:rounded-2xl sm:px-[18px] sm:py-3.5">
        <InformationCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 stroke-2 sm:h-[15px] sm:w-[15px]" />
        <p className="text-[13px] leading-[18px] sm:text-[13.5px] sm:leading-5">
          Express scripts can&apos;t cover controlled or Schedule 8 medicines —
          including opioids, codeine painkillers, benzodiazepines, sleeping
          tablets, ADHD medicines and steroids.
        </p>
      </div>
    </motion.div>
  )
}

function MedicineSearch({
  isFetching,
  search,
  setSearch,
}: {
  isFetching: boolean
  search: string
  setSearch: (search: string) => void
}) {
  return (
    <motion.div variants={sectionMotion} className="mt-[15px] sm:mt-[18px]">
      <label className="sr-only" htmlFor="medicine-search">
        Search by medicine or brand name
      </label>
      <div
        className={clsx(
          'flex h-[50px] items-center gap-[9px] rounded-[14px] bg-[#F4F4F6] px-4 transition-colors duration-300 sm:h-[52px] sm:gap-2.5 sm:px-[18px]',
          search && 'ring-[1.5px] ring-inset ring-[#0B0B0F]',
        )}
      >
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-[#82828B]" />
        <input
          id="medicine-search"
          className="min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-[14px] leading-5 text-[#0B0B0F] outline-none ring-0 placeholder:text-[#A0A0A8] focus:ring-0 sm:text-[15px]"
          name="medicine-search"
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search medicine or brand"
          type="search"
          value={search}
        />
        <AnimatePresence initial={false}>
          {isFetching && (
            <motion.span
              animate={{ opacity: 1 }}
              className="flex h-4 w-4 shrink-0 items-center justify-center"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
            >
              <Spinner />
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

function ConsultationFallback() {
  const reduceMotion = useReducedMotion()

  return (
    <motion.div
      className="mt-3.5 flex flex-col gap-[3px] rounded-[14px] bg-[#F4F4F6] px-4 py-3.5"
      initial={reduceMotion ? false : 'hidden'}
      variants={viewportItemMotion}
      viewport={{ once: true, amount: 0.12, margin: '0px 0px 96px 0px' }}
      whileInView="visible"
    >
      <p className="text-[13px] leading-[18px] text-[#5C5C64] sm:text-[13.5px] sm:leading-[19px]">
        Can&apos;t find your medicine? Not everything can be requested through
        express script.
      </p>
      <Link
        className="text-[13px] font-semibold leading-[17px] text-[#2563EB] sm:text-[13.5px] sm:leading-[18px]"
        href="/telehealth-consultation/start"
      >
        Start a general consultation —{' '}
        <PricingValue name="generalConsultation" /> →
      </Link>
    </motion.div>
  )
}

function CategoryBrowser({ categories }: { categories: MedicineCategory[] }) {
  return (
    <motion.div
      animate="visible"
      className="mt-3.5"
      initial="hidden"
      variants={listMotion}
    >
      <motion.h2
        variants={itemMotion}
        className="mb-3 text-[16px] font-semibold leading-[22px] tracking-[-0.01em] text-[#0B0B0F] sm:mb-3.5 sm:text-[16.5px] sm:leading-[23px]"
      >
        Browse by condition
      </motion.h2>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-3">
        {categories.map((category) => (
          <motion.div
            key={`${category.alias}-${category.name}`}
            variants={itemMotion}
          >
            <Link
              className="group flex min-h-[46px] items-center rounded-[14px] bg-[#F4F4F6] px-4 py-3 text-[14.5px] font-semibold leading-[19px] text-[#0B0B0F] transition-colors duration-300 hover:bg-[#ECECEF] sm:min-h-[51px] sm:rounded-2xl sm:px-5 sm:py-4 sm:text-[15px]"
              href={`/prescriptions-online/categories/${category.alias}`}
            >
              {category.name}
              <ChevronDownIcon className="ml-auto h-[14px] w-[14px] -rotate-90 text-[#A0A0A8] transition-colors duration-300 group-hover:text-[#5C5C64] sm:h-[15px] sm:w-[15px]" />
            </Link>
          </motion.div>
        ))}
      </div>
      <ConsultationFallback />
    </motion.div>
  )
}

function NoResults({ searchTerm }: { searchTerm: string }) {
  return (
    <motion.div variants={stateMotion}>
      <div className="flex flex-col items-center gap-[9px] pb-[26px] pt-11 sm:gap-2.5 sm:pb-[34px] sm:pt-14">
        <svg
          aria-hidden="true"
          className="h-[26px] w-[26px] shrink-0 sm:h-7 sm:w-7"
          viewBox="0 0 24 24"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
            fill="none"
            stroke="#A0A0A8"
            strokeWidth="1.7"
          />
          <path
            d="M21 21l-4.35-4.35M8.5 8.5l5 5M13.5 8.5l-5 5"
            fill="none"
            stroke="#A0A0A8"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.7"
          />
        </svg>
        <p className="text-[14.5px] font-semibold leading-[19px] text-[#0B0B0F] sm:text-[15.5px] sm:leading-5">
          No matches for &quot;{searchTerm}&quot;
        </p>
        <p className="max-w-[290px] text-center text-[13px] leading-[18px] text-[#82828B] sm:max-w-[400px] sm:text-[13.5px] sm:leading-[19px]">
          Check the spelling, or try the medicine&apos;s generic name — brand
          and generic names both work.
        </p>
      </div>
      <ConsultationFallback />
    </motion.div>
  )
}

export function MedicineList({
  category,
  groups,
  onSelectionChange,
  searchTerm,
  showCategoryNavigation = true,
  showConsultationFallback = true,
}: {
  category?: MedicineCategory
  groups: MedicineWithKeyAcg[]
  onSelectionChange?: (selection: MedicineSelection | null) => void
  searchTerm?: string
  showCategoryNavigation?: boolean
  showConsultationFallback?: boolean
}) {
  const reduceMotion = useReducedMotion()
  const [expandedMedicineId, setExpandedMedicineId] = useState<string | null>(
    null,
  )
  const [selection, setSelection] = useState<MedicineSelection | null>(null)

  useEffect(() => {
    setExpandedMedicineId(null)
    setSelection(null)
    onSelectionChange?.(null)
  }, [category?.alias, onSelectionChange, searchTerm])

  const buildHref = (
    medicine: CuratedMedicineConcise,
    preferredBrand?: string,
  ) => {
    const categoryAlias = category?.alias ?? medicine.categories[0]?.alias
    const brand = preferredBrand ? `/${encodeURIComponent(preferredBrand)}` : ''

    return `/prescriptions-online/categories/${categoryAlias}/${medicine.alias}${brand}?p=t`
  }

  const updateSelection = (
    medicine: CuratedMedicineConcise,
    preferredBrand?: string,
  ) => {
    const nextSelection = {
      href: buildHref(medicine, preferredBrand),
      medicine,
      medicineId: medicine.id,
      preferredBrand,
    }
    setSelection(nextSelection)
    onSelectionChange?.(nextSelection)
  }

  const toggleMedicine = (medicine: CuratedMedicineConcise) => {
    const isClosing = expandedMedicineId === medicine.id
    setExpandedMedicineId(isClosing ? null : medicine.id)
    if (selection?.medicineId !== medicine.id || isClosing) {
      setSelection(null)
      onSelectionChange?.(null)
    }
  }

  const medicineCount = groups.reduce(
    (count, group) => count + group.medicines.length,
    0,
  )
  let medicineRevealIndex = 0

  return (
    <div className="mt-3 flex flex-col gap-2.5 sm:mt-3.5 sm:gap-3.5">
      {searchTerm ? (
        <motion.p
          animate="visible"
          initial={reduceMotion ? false : 'hidden'}
          variants={itemMotion}
          className="text-[13px] leading-[17px] text-[#82828B] sm:text-[13.5px] sm:leading-[18px]"
        >
          {`${medicineCount} ${
            medicineCount === 1 ? 'medicine matches' : 'medicines match'
          } "${searchTerm}"`}
        </motion.p>
      ) : showCategoryNavigation ? (
        <motion.div
          animate="visible"
          initial={reduceMotion ? false : 'hidden'}
          variants={itemMotion}
          className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3.5"
        >
          <Link
            className="text-[13px] font-semibold leading-[17px] text-[#2563EB] sm:text-[13.5px] sm:leading-[18px]"
            href="/prescriptions-online/categories"
          >
            ← All categories
          </Link>
          <h2 className="text-[16px] font-semibold leading-[22px] tracking-[-0.01em] text-[#0B0B0F] sm:text-[16.5px] sm:leading-[23px]">
            {category?.name}
          </h2>
        </motion.div>
      ) : null}

      {groups.map((group, groupIndex) => (
        <div className="contents" key={`${group.acg}-${groupIndex}`}>
          <motion.h3
            className={clsx(
              'text-[12.5px] font-semibold leading-4 text-[#82828B] sm:text-[13px] sm:leading-[17px]',
              groupIndex > 0 && 'mt-1 sm:mt-1.5',
            )}
            initial={reduceMotion ? false : 'hidden'}
            variants={viewportItemMotion}
            viewport={{
              once: true,
              amount: 0.2,
              margin: '0px 0px 96px 0px',
            }}
            whileInView="visible"
          >
            {group.acg}
          </motion.h3>
          {group.medicines.map((medicine) => {
            const expanded = expandedMedicineId === medicine.id
            const revealDelay = Math.min(medicineRevealIndex, 3) * 0.025
            medicineRevealIndex += 1
            const orderedBrandNames = searchTerm
              ? [...medicine.brandNames].sort((left, right) => {
                  const query = searchTerm.toLowerCase()
                  return (
                    Number(right.toLowerCase().includes(query)) -
                    Number(left.toLowerCase().includes(query))
                  )
                })
              : medicine.brandNames
            return (
              <motion.article
                custom={revealDelay}
                key={medicine.id}
                className={clsx(
                  'overflow-hidden rounded-[14px] bg-[#F4F4F6] transition-shadow duration-500 ease-[cubic-bezier(0.22,0.55,0.28,1)] sm:rounded-2xl',
                  expanded && 'ring-[1.5px] ring-inset ring-[#0B0B0F]',
                )}
                initial={reduceMotion ? false : 'hidden'}
                variants={viewportItemMotion}
                viewport={{
                  once: true,
                  amount: 0.12,
                  margin: '0px 0px 96px 0px',
                }}
                whileInView="visible"
              >
                <button
                  aria-expanded={expanded}
                  className="flex w-full items-start gap-3 px-4 py-3.5 text-left sm:items-center sm:gap-4 sm:px-5 sm:py-4"
                  onClick={() => toggleMedicine(medicine)}
                  type="button"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14.5px] font-semibold leading-[19px] text-[#0B0B0F] sm:text-[15.5px] sm:leading-5">
                      {medicine.displayName}
                    </span>
                    <span className="mt-0.5 block text-[12.5px] leading-4 text-[#82828B] sm:mt-[3px] sm:text-[13px] sm:leading-[18px]">
                      {medicine.quantity} per script · no repeats
                    </span>
                    <AnimatePresence initial={false}>
                      {!expanded && medicine.brandNames.length > 0 && (
                        <motion.span
                          animate={{
                            gridTemplateRows: '1fr',
                            opacity: 1,
                          }}
                          className="grid text-[12.5px] leading-[19px] text-[#5C5C64] sm:text-[13px] sm:leading-5"
                          exit={{
                            gridTemplateRows: '0fr',
                            opacity: 0,
                            transition: {
                              delay: reduceMotion ? 0 : 0.08,
                              duration: reduceMotion ? 0 : 0.26,
                              ease: softEditorialEaseInOut,
                            },
                          }}
                          initial={{
                            gridTemplateRows: '0fr',
                            opacity: 0,
                          }}
                          transition={{
                            delay: reduceMotion ? 0 : 0.1,
                            duration: reduceMotion ? 0 : 0.3,
                            ease: softEditorialEase,
                          }}
                        >
                          <span className="min-h-0 overflow-hidden">
                            {orderedBrandNames.join(' · ')}
                          </span>
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </span>
                  <motion.span
                    animate={{
                      color: expanded ? '#0B0B0F' : '#A0A0A8',
                      rotate: expanded ? 180 : 0,
                    }}
                    className="mt-[3px] h-3.5 w-3.5 shrink-0 sm:mt-0 sm:h-[15px] sm:w-[15px]"
                    transition={{
                      duration: reduceMotion ? 0 : 0.42,
                      ease: softEditorialEaseInOut,
                    }}
                  >
                    <ChevronDownIcon className="h-full w-full" />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      animate={{ gridTemplateRows: '1fr' }}
                      className="grid"
                      exit={{ gridTemplateRows: '0fr' }}
                      initial={{ gridTemplateRows: '0fr' }}
                      transition={{
                        duration: reduceMotion ? 0 : 0.52,
                        ease: softEditorialEaseInOut,
                      }}
                    >
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="min-h-0 overflow-hidden"
                        exit={{
                          opacity: 0,
                          transition: {
                            duration: reduceMotion ? 0 : 0.18,
                            ease: softEditorialEaseInOut,
                          },
                        }}
                        initial={{ opacity: 0 }}
                        transition={{
                          delay: reduceMotion ? 0 : 0.08,
                          duration: reduceMotion ? 0 : 0.3,
                          ease: softEditorialEase,
                        }}
                      >
                        <div className="mx-4 border-t border-[#E2E2E7] pb-3.5 pt-2.5 sm:mx-5 sm:pb-4 sm:pt-3">
                          <p className="text-[13.5px] font-semibold leading-[18px] text-[#0B0B0F] sm:text-[14px] sm:leading-[19px]">
                            Which brand do you prefer?
                          </p>
                          <p className="mt-0.5 text-[12px] leading-4 text-[#82828B] sm:text-[12.5px] sm:leading-[17px]">
                            If you pick one, the pharmacy dispenses that brand
                            where it can.
                          </p>
                          <motion.div
                            animate="visible"
                            className="mt-2.5 flex flex-col gap-2.5 sm:mt-3 sm:gap-3"
                            initial="hidden"
                            variants={brandListMotion}
                          >
                            {[
                              ...orderedBrandNames.map((brandName) => ({
                                brandName,
                                label: brandName,
                              })),
                              {
                                brandName: undefined,
                                label: 'No preference',
                              },
                            ].map(({ brandName, label }) => {
                              const selected =
                                selection?.medicineId === medicine.id &&
                                selection.preferredBrand === brandName

                              return (
                                <motion.button
                                  key={brandName ?? 'no-preference'}
                                  variants={brandItemMotion}
                                  className={clsx(
                                    'flex min-h-[41px] w-full items-center rounded-xl bg-white px-3.5 py-[11px] text-left text-[13.5px] leading-[17px] text-[#5C5C64] sm:min-h-[44px] sm:px-4 sm:py-3 sm:text-[14px] sm:leading-[18px]',
                                    selected &&
                                      'text-[#0B0B0F] ring-[1.5px] ring-inset ring-[#0B0B0F]',
                                  )}
                                  onClick={() =>
                                    updateSelection(medicine, brandName)
                                  }
                                  type="button"
                                >
                                  {label}
                                  <RadioMark selected={selected} />
                                </motion.button>
                              )
                            })}
                          </motion.div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            )
          })}
        </div>
      ))}
      {showConsultationFallback && <ConsultationFallback />}
    </div>
  )
}

function FloatingNextAction({
  bottomActionsInView,
  selection,
}: {
  bottomActionsInView: boolean
  selection: MedicineSelection | null
}) {
  const reduceMotion = useReducedMotion()

  return (
    <AnimatePresence initial={false}>
      {selection && !bottomActionsInView && (
        <motion.div
          animate={{ opacity: 1 }}
          className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-5"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          style={{
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          }}
          transition={{
            duration: reduceMotion ? 0 : 0.34,
            ease: softEditorialEase,
          }}
        >
          <div className="relative flex w-full max-w-[420px] justify-center">
            <span
              aria-hidden="true"
              className="absolute inset-x-4 -bottom-2 h-[78px] rounded-full bg-white/90 blur-xl"
            />
            <span
              aria-hidden="true"
              className="absolute inset-x-12 bottom-0 h-[54px] rounded-full bg-[#DDE7FF]/55 blur-2xl"
            />
            <Link
              className="pointer-events-auto relative flex h-[47px] w-full max-w-[335px] items-center justify-center gap-[9px] rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white shadow-[0_18px_48px_rgba(11,11,15,0.20),0_4px_14px_rgba(11,11,15,0.10)] transition-colors duration-300 hover:bg-[#242428] sm:w-auto sm:min-w-[136px]"
              href={selection.href}
            >
              Next
              <ArrowIcon direction="right" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function QuizActions({
  nextButtonRef,
  selection,
  showBack,
}: {
  nextButtonRef: RefObject<HTMLDivElement | null>
  selection: MedicineSelection | null
  showBack: boolean
}) {
  const router = useRouter()

  const backButton = (
    <button
      className="flex h-[47px] items-center justify-center gap-[9px] rounded-full border-[1.5px] border-[#0B0B0F38] px-[26px] text-[14.5px] font-semibold leading-[18px] text-[#0B0B0F] transition-colors duration-300 hover:border-[#0B0B0F70] max-sm:w-full"
      onClick={() => router.back()}
      type="button"
    >
      <ArrowIcon direction="left" />
      Back
    </button>
  )

  const nextButton = selection ? (
    <Link
      className="flex h-[47px] items-center justify-center gap-[9px] rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white transition-colors duration-300 hover:bg-[#242428] max-sm:w-full"
      href={selection.href}
    >
      Next
      <ArrowIcon direction="right" />
    </Link>
  ) : (
    <button
      className="flex h-[47px] items-center justify-center gap-[9px] rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white opacity-40 max-sm:w-full"
      disabled
      type="button"
    >
      Next
      <ArrowIcon direction="right" />
    </button>
  )

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="mx-auto flex w-full max-w-[335px] flex-col gap-2.5 px-0 pb-9 pt-7 sm:max-w-none sm:flex-row sm:justify-center sm:gap-3 sm:pb-[72px] sm:pt-9"
      initial={{ opacity: 0 }}
      transition={{
        delay: 0.18,
        duration: softMotionDuration.handoff,
        ease: softEditorialEase,
      }}
    >
      {showBack && <div className="contents max-sm:hidden">{backButton}</div>}
      <div ref={nextButtonRef} className="flex max-sm:w-full">
        {nextButton}
      </div>
      {showBack && <div className="contents sm:hidden">{backButton}</div>}
    </motion.div>
  )
}

export default function MedicineQuizScreen({
  categories,
  category,
  groups,
}: Props) {
  const api = useApiClient()
  const animateSharedChrome = useAnimateMedicineQuizChrome()
  const reduceMotion = useReducedMotion()
  const [search, setSearch] = useQueryState('q')
  const debouncedSearch = useDebounce(search?.trim() ?? '', 300)
  const [selection, setSelection] = useState<MedicineSelection | null>(null)
  const bottomNextButtonRef = useRef<HTMLDivElement>(null)
  const bottomActionsInView = useInView(bottomNextButtonRef, { amount: 0.05 })
  const hasSearch = (search?.trim().length ?? 0) >= 3
  const settledSearch = debouncedSearch.length >= 3 ? debouncedSearch : ''
  const query = useQuery({
    ...SearchMedicine.query(api, {
      word: settledSearch,
    }),
    enabled: settledSearch.length >= 3,
  })

  const resultGroups = query.data?.groups
  const stateKey = useMemo(() => {
    if (!hasSearch)
      return category ? `category-${category.alias}` : 'categories'
    if (!resultGroups) return 'search-loading'
    return resultGroups.length > 0
      ? `search-results-${settledSearch}`
      : `search-empty-${settledSearch}`
  }, [category, hasSearch, resultGroups, settledSearch])

  useEffect(() => {
    setSelection(null)
  }, [stateKey])

  const showSearchExplanation =
    hasSearch && Boolean(resultGroups) && resultGroups?.length === 0

  let content: ReactNode
  if (hasSearch) {
    if (!resultGroups) {
      content = (
        <motion.div
          variants={stateMotion}
          className="flex min-h-48 items-center justify-center"
        >
          <Spinner />
        </motion.div>
      )
    } else if (resultGroups.length > 0) {
      content = (
        <MedicineList
          groups={orderSearchGroups(resultGroups)}
          onSelectionChange={setSelection}
          searchTerm={settledSearch}
        />
      )
    } else {
      content = <NoResults searchTerm={settledSearch} />
    }
  } else if (category && groups) {
    content = (
      <MedicineList
        category={category}
        groups={groups}
        onSelectionChange={setSelection}
      />
    )
  } else {
    content = <CategoryBrowser categories={categories ?? []} />
  }

  return (
    <main className="min-h-svh shrink-0 overflow-x-clip bg-white font-sans text-[#0B0B0F]">
      <div
        className={clsx(
          'mx-auto w-full max-w-[720px] px-5 pt-[107px] lg:pt-[159px]',
          !category && !hasSearch && 'pb-9 sm:pb-[72px]',
        )}
      >
        <motion.div
          animate="visible"
          initial={reduceMotion || !animateSharedChrome ? false : 'hidden'}
          variants={pageMotion}
        >
          <motion.header
            variants={sectionMotion}
            className="mx-auto w-full max-w-[680px] pb-3"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2">
                <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#1F8A70]" />
                <p className="truncate text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.025em] text-[#17614F] sm:text-[13px] sm:leading-4">
                  <span className="sm:hidden">
                    Request care · Prescriptions
                  </span>
                  <span className="hidden sm:inline">
                    Request care · Online prescriptions
                  </span>
                </p>
              </div>
            </div>
            <h1 className="mt-3 font-display text-[26px] font-bold leading-[33px] tracking-[-0.02em] sm:mt-4 sm:text-[34px] sm:leading-[42px]">
              Find your medicine.
            </h1>
            {showSearchExplanation && (
              <motion.p
                animate={{ opacity: 1 }}
                className="mt-2 text-[13px] leading-[18px] text-[#82828B] sm:hidden"
                initial={{ opacity: 0 }}
                transition={{
                  duration: softMotionDuration.panel,
                  ease: softEditorialEase,
                }}
              >
                Express scripts cover a set list of everyday medicines, each as
                a single supply with no repeats. The doctor still reviews every
                request.
              </motion.p>
            )}
          </motion.header>

          <div className="mx-auto w-full max-w-[680px]">
            <RestrictedMedicineNotice
              listLinkVisibility={
                !category && !hasSearch
                  ? 'always'
                  : showSearchExplanation
                    ? 'mobile'
                    : 'never'
              }
            />
            <MedicineSearch
              isFetching={query.isFetching}
              search={search ?? ''}
              setSearch={(value) => void setSearch(value || null)}
            />
          </div>
        </motion.div>

        <div className="mx-auto w-full max-w-[680px]">
          <AnimatePresence initial={!reduceMotion} mode="wait">
            <motion.div
              animate="visible"
              exit="exit"
              initial="hidden"
              key={stateKey}
              variants={stateMotion}
            >
              {content}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <FloatingNextAction
        bottomActionsInView={bottomActionsInView}
        selection={selection}
      />
      {(category || hasSearch) && (
        <QuizActions
          nextButtonRef={bottomNextButtonRef}
          selection={selection}
          showBack={Boolean(category)}
        />
      )}
    </main>
  )
}
